import {
  Datagrid,
  List,
  Pagination,
  Empty,
  Show,
  SimpleShowLayout,
  TabbedShowLayout,
  TextField,
  ImageField,
  FunctionField,
  BooleanField,
  TopToolbar,
  EditButton,
  useNotify,
} from 'react-admin';
import {
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField as TxtField,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useRecordContext } from 'ra-core';
import { DateTime } from 'luxon';
import { isDateOverdue } from '../../utils/is-date-overdue';
import { useResourceAccess } from '../../hooks/useAccess';
import { postJson } from '../../utils/api';
import { IS_REVOKED_RP } from '../../utils/shared';

const TransactionList = () => {
  const params = useParams();
  const record = useRecordContext();

  const userId = params.userId ?? record?.id;
  if (!userId) {
    return <Empty />;
  }

  return (
    <List
      resource="user-transactions"
      queryOptions={{ meta: { userId } }}
      hasCreate={false}
      perPage={100}
      pagination={<Pagination rowsPerPageOptions={[10, 25, 50, 100]} />}
      title="/Transactions"
    >
      <Datagrid bulkActionButtons={false}>
        <TextField source="id" />
        <TextField source="userId" />
        <TextField source="reason" />
        <FunctionField
          label="Details"
          render={(currentRecord: any) => {
            const showGiftCard =
              currentRecord.details?.giftCard?.assets?.giftCardUrl;
            if (showGiftCard) {
              return (
                <ImageField
                  source="details.giftCard.assets.giftCardUrl"
                  className="gift-card-img"
                  label="Gift Card"
                  headerClassName="head-center"
                  record={currentRecord} // Pass the current row's record explicitly to ImageField
                />
              );
            } else {
              const details = [];

              if (currentRecord.details?.adminUser) {
                const { firstName, lastName } = currentRecord.details.adminUser;
                details.push([firstName, lastName].join(' '));
              }
              if (currentRecord.details?.reason) {
                details.push(currentRecord.details?.reason);
              }

              return details.join(' ');
            }
          }}
        />

        <TextField source="amount" />
        <TextField source="status" />
        <FunctionField
          label="Created At"
          render={({ createdAt }: any) =>
            DateTime.fromMillis(createdAt).toFormat('dd/MM/yyyy HH:mm')
          }
        />
      </Datagrid>
    </List>
  );
};

const UserDetailsTab = ({
  rewardPoints,
  setRewardPoints,
}: {
  rewardPoints: number;
  setRewardPoints: (points: number) => void;
}) => {
  const record = useRecordContext();

  useEffect(() => {
    if (record) {
      setRewardPoints(record.rewardPoints || 0);
    }
  }, [record, setRewardPoints]);

  return (
    <Grid container>
      <Grid xs={8} item={true}></Grid>
      <Grid container item xs={8} spacing={2}>
        <Grid item xs={3}>
          <SimpleShowLayout>
            <TextField source="id" />
            <FunctionField
              label="Cashback redemption feature is enabled for this user"
              render={({ csbEnabled }: any) => (
                <BooleanField
                  record={{ csbEnabled: Boolean(csbEnabled) }}
                  source="csbEnabled"
                />
              )}
            />
            <FunctionField
              label="Gift Cards redemption feature is enabled for this user"
              render={({ gcdEnabled }: any) => (
                <BooleanField
                  record={{ gcdEnabled: Boolean(gcdEnabled) }}
                  source="gcdEnabled"
                />
              )}
            />
            <FunctionField label="Reward Points" render={() => rewardPoints} />
            <FunctionField
              label="User subscription is cancelled"
              render={({ isCancelled }: any) => (
                <BooleanField
                  record={{ isCancelled: Boolean(isCancelled) }}
                  source="isCancelled"
                />
              )}
            />
            <FunctionField
              label="Revoked RP"
              render={({ isCancelled }: any) => (
                <BooleanField
                  record={{ isCancelled: isCancelled === IS_REVOKED_RP }}
                  source="isCancelled"
                />
              )}
            />
            <FunctionField
              label="Spent daily £"
              render={({ lastSpent, spentGbpDaily }: any) => {
                const needToResetDailySpend = isDateOverdue(lastSpent, 'day');

                return needToResetDailySpend ? 0 : spentGbpDaily;
              }}
            />
            <FunctionField
              label="Spent monthly £"
              render={({ lastSpent, spentGbpMonthly }: any) => {
                const needToResetDailySpend = isDateOverdue(lastSpent, 'day');

                return needToResetDailySpend ? 0 : spentGbpMonthly;
              }}
            />
            <FunctionField
              label="Last Spent At"
              render={({ lastSpent }: any) =>
                (lastSpent ?? 0) !== 0
                  ? DateTime.fromMillis(lastSpent ?? 0).toFormat(
                      'dd/MM/yyyy HH:mm',
                    )
                  : 'never'
              }
            />
          </SimpleShowLayout>
        </Grid>
        <Grid item xs={5}></Grid>
      </Grid>
    </Grid>
  );
};

type AddRewardPointsDialogProps = {
  open: boolean;
  onClose: () => void;
  onRewardPointsAdded: (newRewardPoints: number) => void;
};

const AddRewardPointsDialog: React.FC<AddRewardPointsDialogProps> = ({
  open,
  onClose,
  onRewardPointsAdded,
}) => {
  const record = useRecordContext();
  const [points, setPoints] = useState('');
  const [reason, setReason] = useState('');
  const notify = useNotify();

  const handleSubmit = async () => {
    try {
      const response = await postJson(
        `/admin/user/${record.id}/add-reward-points`,
        {
          rewardPoints: Number(points),
          reason,
        },
      );

      const responseBody = await response.json();

      const updatedRewardPoints = responseBody.data.rewardPoints;

      notify('Reward points added successfully', { type: 'success' });

      onRewardPointsAdded(updatedRewardPoints);

      onClose();
    } catch (error) {
      console.error(error);
      notify('Failed to add reward points', { type: 'error' });
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Add Reward Points</DialogTitle>
      <DialogContent>
        <TxtField
          label="Reward Points"
          type="number"
          fullWidth
          value={points}
          onChange={(e) => setPoints(e.target.value)}
          margin="normal"
          required
        />
        <TxtField
          label="Reason"
          fullWidth
          multiline
          rows={4}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          margin="normal"
          required
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} color="primary" variant="contained">
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const UserActions = ({
  onRewardPointsAdded,
}: {
  onRewardPointsAdded: AddRewardPointsDialogProps['onRewardPointsAdded'];
}) => {
  const { canEditUser } = useResourceAccess();
  const [openDialog, setOpenDialog] = useState(false);

  if (!canEditUser()) {
    return <></>;
  }

  const actions = [
    <EditButton key="edit" />,
    <Button
      key="add-reward-points"
      onClick={() => setOpenDialog(true)}
      color="primary"
      variant="contained"
    >
      Add Reward Points
    </Button>,
  ];

  return (
    <>
      <TopToolbar>{actions}</TopToolbar>
      <AddRewardPointsDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        onRewardPointsAdded={onRewardPointsAdded} // Pass the setter function to update reward points
      />
    </>
  );
};

export const UserDetails = () => {
  const record = useRecordContext();
  const [rewardPoints, setRewardPoints] = useState(record?.rewardPoints || 0);

  return (
    <Show actions={<UserActions onRewardPointsAdded={setRewardPoints} />}>
      <TabbedShowLayout>
        <TabbedShowLayout.Tab label="General">
          <UserDetailsTab
            rewardPoints={rewardPoints}
            setRewardPoints={setRewardPoints}
          />
        </TabbedShowLayout.Tab>
        <TabbedShowLayout.Tab label="Transactions">
          <TransactionList />
        </TabbedShowLayout.Tab>
      </TabbedShowLayout>
    </Show>
  );
};
